import React, { useState } from 'react';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const inputStyle = {
  height: '3rem',
  padding: '0.75rem',
  borderRadius: '0.75rem',
  border: '1px solid #d1d5db',
  outline: 'none',
  fontSize: '0.875rem',
  backgroundColor: 'rgba(255, 255, 255, 0.82)',
  backdropFilter: 'blur(4px)',
  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
  width: '100%',
};

const labelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  color: '#374151',
  fontSize: '0.875rem',
  fontWeight: '500',
};

const ForgotPasswordPage = () => {
  const [step, setStep] = useState('email'); // 'email' | 'reset' | 'done'
  const [email, setEmail] = useState('');
  const [cacheKey, setCacheKey] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}login/forgot-password/send-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        setCacheKey(data.cache_key);
        setStep('reset');
      } else {
        setError(data.email || data.detail || 'Failed to send OTP. Please check the email address.');
      }
    } catch (err) {
      setError(`Failed to connect to the server: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp) { setError('Please enter the OTP sent to your email.'); return; }
    if (!newPassword) { setError('Please enter a new password.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}login/forgot-password/reset/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, cache_key: cacheKey, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setStep('done');
      } else {
        const errorMsg = data.new_password || data.detail || 'Failed to reset password. Please try again.';
        setError(Array.isArray(errorMsg) ? errorMsg.join(' ') : errorMsg);
      }
    } catch (err) {
      setError(`Failed to connect to the server: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <img
        src="/view.jpg"
        alt="Background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: 1,
          zIndex: -1,
        }}
      />

      <div
        style={{
          width: '100%',
          maxWidth: '500px',
          backgroundColor: '#ffffffe6',
          opacity: 0.9,
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '1rem',
          boxShadow: '0 0 15px 3px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            Reset Your Password
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {step === 'email' && "Enter your account email and we'll send you an OTP."}
            {step === 'reset' && `Enter the OTP sent to ${email} and choose a new password.`}
            {step === 'done' && 'Your password has been reset.'}
          </p>
        </div>

        {step === 'email' && (
          <form onSubmit={handleSendOtp} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={labelStyle}>
                <FaEnvelope size={16} style={{ color: '#9ca3af' }} />
                Email Address
              </label>
              <input
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </div>

            {error && <div style={{ color: '#dc3545', fontSize: '0.875rem' }}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: '3rem',
                background: 'linear-gradient(to right, #4f46e5, #a855f7)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '0.75rem',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleResetPassword} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={labelStyle}>OTP Code</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                autoComplete="one-time-code"
                name="otp-code"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', position: 'relative' }}>
              <label style={labelStyle}>
                <FaLock size={16} style={{ color: '#9ca3af' }} />
                New Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                name="new-password"
                style={{ ...inputStyle, padding: '0.75rem 2.5rem 0.75rem 0.75rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '65%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                name="confirm-new-password"
                style={inputStyle}
              />
            </div>

            {error && <div style={{ color: '#dc3545', fontSize: '0.875rem' }}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: '3rem',
                background: 'linear-gradient(to right, #4f46e5, #a855f7)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '0.75rem',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div style={{ textAlign: 'center' }}>
              <a
                href="/ForgotPassword"
                style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: '500', fontSize: '0.875rem' }}
                onClick={(e) => { e.preventDefault(); setStep('email'); setError(''); }}
              >
                Didn't get a code? Resend
              </a>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/LoginPage')}
              style={{
                width: '100%',
                height: '3rem',
                background: 'linear-gradient(to right, #4f46e5, #a855f7)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '0.75rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Back to Sign In
            </button>
          </div>
        )}

        {step !== 'done' && (
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <a
              href="/LoginPage"
              style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: '500', fontSize: '0.875rem' }}
              onClick={(e) => { e.preventDefault(); navigate('/LoginPage'); }}
            >
              Back to Sign In
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
